import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Page4.css";

import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { trackNewsletterSignup, trackInstallClick } from "../utils/gtmTracking";

// Assets
import bottomImagePage4 from "../assets/images/Page4_Bottom_Bit@.png";
import SignUpFrame from "../assets/images/Page4_Signup_Frame@.png";
import SubmitButton from "../assets/images/submitBtn.png";
import Sean from "../assets/images/Page4_Sean@.png";
import Margarita from "../assets/images/Page4_Margarita@.png";
import Phil from "../assets/images/Page4_Phil@.png";
import Itzamna from "../assets/images/Page4_Itzamna@.png";
import Skeleton from "../assets/images/Page4_Skeleton.png";
import Icon from "../assets/images/Page4_Spurpunk_icon@.png";
import Page4DottedLine from "../assets/images/Page4_Dotted_Line@.png";
import Page4Arrowhead from "../assets/images/Page4_Arrowhead@.png";
import AppStore from "../assets/images/AppleStore_BTN@.svg";
import PlayStore from "../assets/images/GooglePlay_BTN@.png";
import Cactus from "../assets/images/map-cactus2-icon_COPY@.png";
import Grass from "../assets/images/Map-grass-icon2@.png";
import Landmass from "../assets/images/Page4_Landmass_COPY@.png";
import FilagreeLower from "../assets/images/filagree_lower@.png";
import FilagreeLowerCopy from "../assets/images/filagree_lower_COPY@.png";
import TownClearedStarFrame from "../assets/images/townclearedstarframe@.png";
import TownClearedStar from "../assets/images/townclearedstar@.png";
import TownClearedStarOne from "../assets/images/townclearedstar1_COPY@.png";
import TownClearedStarTwo from "../assets/images/townclearedstar2_COPY@.png";
import Laurels from "../assets/images/Laurels@.png";
import LaurelsReverse from "../assets/images/Laurels_reverse@.png";

const SIGNUP_STAGE_WIDTH = 1090;
const SIGNUP_STAGE_HEIGHT = 592;
const SIGNUP_STAGE_MAX_SCALE = 1;

const AWARDS_STAGE_WIDTH = 1090;
const AWARDS_SCALE_MIN = 0.65;
const AWARDS_SCALE_MAX = 1.75;

const Page4 = () => {
  const page4Ref = useRef(null);
  const awardsBannerRef = useRef(null);
  const signupSectionRef = useRef(null);
  const bottomSectionRef = useRef(null);
  const characterAnimationFrameRef = useRef(null);
  const [signupScale, setSignupScale] = useState(1);

  const [formData, setFormData] = useState({ name: "", email: "" });
  const [message, setMessage] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("Submitting...");

    const { name, email } = formData;

    if (!name || !email) {
      setMessage("Please fill in both fields.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage("Please enter a valid email address.");
      return;
    }

    setMessage("✅ Thank you for subscribing!");
    setFormData({ name: "", email: "" });

    const firestorePromise = addDoc(collection(db, "signups"), {
      name: name.trim(),
      email: email.trim(),
      createdAt: serverTimestamp(),
      source: "Spurpunk",
    });

    const authHeader = "Basic " + btoa("admin_martech" + ":" + "0123406789012");
    const backendPromise = axios.post(
      "https://martech-robotrace-ykgj.onrender.com/submit",
      formData,
      { headers: { Authorization: authHeader } }
    );

    try {
      await Promise.allSettled([firestorePromise, backendPromise]);
      trackNewsletterSignup(email, name, true);
    } catch (error) {
      console.error("Error submitting data:", error);
      trackNewsletterSignup(email, name, false);
      setMessage("Oops, something went wrong. Please try again later.");
    }
  };

  useEffect(() => {
    console.log("Updated message:", message);
  }, [message]);
  useEffect(() => {
  const awardsBanner = awardsBannerRef.current;
  if (!awardsBanner) return undefined;

  const updateAwardsScale = () => {
    const viewportWidth = window.visualViewport?.width || window.innerWidth;
    const nextScale = Math.min(
      AWARDS_SCALE_MAX,
      Math.max(AWARDS_SCALE_MIN, viewportWidth / AWARDS_STAGE_WIDTH)
    );

    awardsBanner.style.setProperty("--awards-scale", nextScale.toFixed(4));
    const nextMobileScale = Math.max(0.62, Math.min(1.05, (viewportWidth - 34) / 540));
    awardsBanner.style.setProperty("--awards-mobile-scale", nextMobileScale.toFixed(4));
  };

  updateAwardsScale();
  window.addEventListener("resize", updateAwardsScale);

  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", updateAwardsScale);
  }

  return () => {
    window.removeEventListener("resize", updateAwardsScale);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", updateAwardsScale);
    }
  };
}, []);
  useEffect(() => {
    const section = signupSectionRef.current;
    if (!section) return undefined;

    const updateSignupScale = () => {
      const availableWidth = section.clientWidth;
      const nextScale = Math.min(
        availableWidth / SIGNUP_STAGE_WIDTH,
        SIGNUP_STAGE_MAX_SCALE
      );
      setSignupScale(nextScale);
    };

    updateSignupScale();

    const resizeObserver = new ResizeObserver(updateSignupScale);
    resizeObserver.observe(section);
    window.addEventListener("resize", updateSignupScale);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateSignupScale);
    };
  }, []);

  useEffect(() => {
    const page4 = page4Ref.current;
    const yellowSection = signupSectionRef.current;
    const bottomSection = bottomSectionRef.current;
    if (!page4 || !yellowSection || !bottomSection) return undefined;

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

    const updateCharacterScrollProgress = () => {
      const viewportHeight = window.innerHeight;

      const getEntranceProgress = (element, startRatio, endRatio) => {
        const rect = element.getBoundingClientRect();
        const referenceHeight = Math.max(
          Math.min(rect.height, viewportHeight),
          1
        );

        const startTop = viewportHeight - referenceHeight * startRatio;
        const endTop = viewportHeight - referenceHeight * endRatio;

        return clamp(
          (startTop - rect.top) / Math.max(startTop - endTop, 1),
          0,
          1
        );
      };

      // Sean enters first: starts at 5% visibility and finishes at 100%.
      const seanRawProgress = getEntranceProgress(yellowSection, 0.05, 1.00);
      const seanProgress =
        seanRawProgress *
        seanRawProgress *
        (3 - 2 * seanRawProgress);
      const seanRemaining =
        seanProgress >= 0.999 ? 0 : 1 - seanProgress;

      // Margarita enters second: starts at 10% visibility and finishes at 100%.
      const margaritaRawProgress = getEntranceProgress(
        yellowSection,
        0.10,
        1.00
      );
      const margaritaProgress =
        margaritaRawProgress *
        margaritaRawProgress *
        (3 - 2 * margaritaRawProgress);
      const margaritaRemaining =
        margaritaProgress >= 0.999 ? 0 : 1 - margaritaProgress;

      // Phil fades in third: starts at 80% visibility and finishes at 100%.
      const philRawProgress = getEntranceProgress(yellowSection, 0.75, 1.00);
      const philProgress =
        philRawProgress *
        philRawProgress *
        (3 - 2 * philRawProgress);

      // Itzamna uses the same responsive visibility logic for the black footer.
      const itzamnaRawProgress = getEntranceProgress(bottomSection, 0.05, 0.90);
      const itzamnaProgress =
        itzamnaRawProgress *
        itzamnaRawProgress *
        (3 - 2 * itzamnaRawProgress);
      const itzamnaRemaining =
        itzamnaProgress >= 0.999 ? 0 : 1 - itzamnaProgress;

      const bottomSkeletonHopHeight =
        Math.sin(itzamnaProgress * Math.PI) *
        Math.min(Math.max(window.innerWidth * 0.045, 28), 75);

      page4.style.setProperty(
        "--sean-scroll-remaining",
        seanRemaining.toFixed(4)
      );
      page4.style.setProperty(
        "--margarita-scroll-remaining",
        margaritaRemaining.toFixed(4)
      );
      page4.style.setProperty(
        "--character-scroll-progress",
        philProgress.toFixed(4)
      );
      page4.style.setProperty(
        "--character-scroll-remaining",
        (1 - philProgress).toFixed(4)
      );
      page4.style.setProperty(
        "--itzamna-scroll-remaining",
        itzamnaRemaining.toFixed(4)
      );
      page4.style.setProperty(
        "--bottom-skeleton-hop-height",
        bottomSkeletonHopHeight.toFixed(2)
      );

      characterAnimationFrameRef.current = null;
    };

    const requestCharacterAnimationUpdate = () => {
      if (characterAnimationFrameRef.current !== null) return;

      characterAnimationFrameRef.current = window.requestAnimationFrame(
        updateCharacterScrollProgress
      );
    };

    updateCharacterScrollProgress();
    window.addEventListener("scroll", requestCharacterAnimationUpdate, {
      passive: true,
    });
    window.addEventListener("resize", requestCharacterAnimationUpdate);

    const animationResizeObserver = new ResizeObserver(
      requestCharacterAnimationUpdate
    );
    animationResizeObserver.observe(yellowSection);
    animationResizeObserver.observe(bottomSection);

    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        "resize",
        requestCharacterAnimationUpdate
      );
    }

    return () => {
      window.removeEventListener("scroll", requestCharacterAnimationUpdate);
      window.removeEventListener("resize", requestCharacterAnimationUpdate);

      animationResizeObserver.disconnect();

      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          requestCharacterAnimationUpdate
        );
      }

      if (characterAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(characterAnimationFrameRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={page4Ref}
      className="container-fluid g-0 d-flex flex-column"
      style={{
        "--character-scroll-progress": 0,
        "--character-scroll-remaining": 1,
        "--itzamna-scroll-remaining": 1,
        "--bottom-skeleton-hop-height": 0,
        "--sean-scroll-remaining": 1,
        "--margarita-scroll-remaining": 1,
      }}
    >
      <section ref={awardsBannerRef} className="topSection awards-banner">
        <div className="awards-stage">
          <img
            src={FilagreeLowerCopy}
            alt=""
            className="awards-gold-line awards-gold-line-left"
          />
          <img
            src={FilagreeLower}
            alt=""
            className="awards-gold-line awards-gold-line-right"
          />

          <div className="awards-banner-section awards-quote-section awards-quote-left">
            <p>
              “A tower defense with a card game twist - original, funny, and great gameplay!”
              <br />
              - Mr. Yamamoto
            </p>
          </div>

          <div className="awards-banner-section awards-center-section">
            <img
              src={TownClearedStarFrame}
              alt="Award frame"
              className="awards-frame"
            />
            <img
              src={TownClearedStar}
              alt=""
              className="awards-star awards-star-left"
            />
            <img
              src={TownClearedStarTwo}
              alt=""
              className="awards-star awards-star-center"
            />
            <img
              src={TownClearedStarOne}
              alt=""
              className="awards-star awards-star-right"
            />

            <img
              src={LaurelsReverse}
              alt=""
              className="award-laurel award-laurel-top-left-reverse"
            />
            <img
              src={Laurels}
              alt=""
              className="award-laurel award-laurel-top-left"
            />
            <img
              src={LaurelsReverse}
              alt=""
              className="award-laurel award-laurel-top-right-reverse"
            />
            <img
              src={Laurels}
              alt=""
              className="award-laurel award-laurel-top-right"
            />
            <img
              src={LaurelsReverse}
              alt=""
              className="award-laurel award-laurel-bottom-left-reverse"
            />
            <img
              src={Laurels}
              alt=""
              className="award-laurel award-laurel-bottom-left"
            />
            <img
              src={LaurelsReverse}
              alt=""
              className="award-laurel award-laurel-bottom-right-reverse"
            />
            <img
              src={Laurels}
              alt=""
              className="award-laurel award-laurel-bottom-right"
            />

            <div className="award-item award-top-left">
              <div className="award-small">MAD INVENTOR GUILD</div>
              <div className="award-big">
                FEWEST
                <br />
                PREVENTABLE
                <br />
                EXPLOSIONS
              </div>
              <div className="award-small">NOMINEE</div>
            </div>

            <div className="award-item award-top-right">
              <div className="award-small">MAGICAL CARDS INC.</div>
              <div className="award-big">
                OUTSTANDING
                <br />
                PROPERTY
                <br />
                DAMAGE
              </div>
              <div className="award-small">WINNER</div>
            </div>

            <div className="award-item award-bottom-left">
              <div className="award-small">ZOMBIE VISITOR GUIDE</div>
              <div className="award-big">
                BEST TOWN
                <br />
                TO INVADE
              </div>
              <div className="award-small">URGHARGHH</div>
            </div>

            <div className="award-item award-bottom-right">
              <div className="award-small">WEIRD WEST SOCIETY</div>
              <div className="award-big">
                MOSTLY
                <br />
                HISTORICALLY
                <br />
                ACCURATE
              </div>
              <div className="award-small">KINDA</div>
            </div>
          </div>

          <div className="awards-banner-section awards-quote-section awards-quote-right">
            <p>
              “A mobile game with simple mechanics and hilarious characters. Weird wild west indeed”
              <br />
              - E. Stonks
            </p>
          </div>
        </div>
      </section>

      <div
        ref={signupSectionRef}
        className="midSection position-relative g-0"
        style={{ height: `${SIGNUP_STAGE_HEIGHT * signupScale}px` }}
      >
        <div
          className="signup-stage"
          style={{ transform: `translateX(-50%) scale(${signupScale})` }}
        >
          <img src={Landmass} alt="Landmass" className="p4-landmass" />
          <img src={Page4DottedLine} alt="Page4 Dotted Line" className="img-fluid page4-dotted-line" />
          <img src={Page4Arrowhead} alt="Arrowhead" className="img-fluid arrow-head" />
          <img src={Icon} alt="Spurpunk icon" className="img-fluid icon" />
          <img
            src={Sean}
            alt="Hero Sean"
            className="img-fluid sean sean-scroll-character"
          />
          <img
            src={Margarita}
            alt="Hero Margarita"
            className="img-fluid margarita margarita-scroll-character"
          />
          <img
            src={Phil}
            alt="Hero Phil"
            className="img-fluid phil phil-scroll-character"
          />
          <img src={Cactus} alt="Cactus" className="img-fluid cactus" />
          <img src={Grass} alt="Grass" className="img-fluid grass" />

          <p className="signup-text">
            Sign up to stay on top of Spurpunk Training
            <br />
            and see which one you might become!
          </p>

          <img src={SignUpFrame} alt="Signup Frame" className="signup-frame" />

          <div className="name-container">
            <label htmlFor="name" className="name-label">NAME</label>
            <input
              type="text"
              id="name"
              name="name"
              className="name-input"
              value={formData.name}
              onChange={handleInputChange}
            />
          </div>

          <div className="email-container">
            <label htmlFor="email" className="email-label">E-MAIL</label>
            <input
              type="text"
              id="email"
              name="email"
              className="email-input"
              value={formData.email}
              onChange={handleInputChange}
            />
            <div className="message-container">
              <span className="email-message">{message}</span>
            </div>
          </div>

          <div className="image-button" onClick={handleSubmit}>
            <img src={SubmitButton} alt="Submit Button" className="submit-button" />
          </div>
        </div>
      </div>

      <div ref={bottomSectionRef} className="bottomSection">
        <img src={bottomImagePage4} alt="Bottom Section" className="img-fluid bottom-img" />
        <img
          src={Skeleton}
          alt="Skeleton"
          className="img-fluid bottom-skeleton bottom-skeleton-scroll-character"
        />

        <div className="download-btns">
          <a
            href="https://play.google.com/store/apps/details?id=com.easleydunnproductions.spurpunk&hl=en_US&gl=US"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              trackInstallClick(
                "google_play",
                "https://play.google.com/store/apps/details?id=com.easleydunnproductions.spurpunk&hl=en_US&gl=US"
              );
              window.open(
                "https://play.google.com/store/apps/details?id=com.easleydunnproductions.spurpunk&hl=en_US&gl=US",
                "_blank",
                "noopener,noreferrer"
              );
            }}
          >
            <img src={PlayStore} alt="Play Store" className="img-fluid playstore" />
          </a>

          <a
            href="https://apps.apple.com/ge/app/spurpunk-td/id1487386202#?platform=iphone"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              trackInstallClick(
                "apple_app_store",
                "https://apps.apple.com/ge/app/spurpunk-td/id1487386202#?platform=iphone"
              );
              window.open(
                "https://apps.apple.com/ge/app/spurpunk-td/id1487386202#?platform=iphone",
                "_blank",
                "noopener,noreferrer"
              );
            }}
          >
            <img src={AppStore} alt="App Store" className="img-fluid appstore" />
          </a>
        </div>

        <img
          src={Itzamna}
          alt="Itzamna"
          className="img-fluid itzamna itzamna-scroll-character"
        />
      </div>

      <footer className="spurpunk-footer">
        <p className="spurpunk-footer-content">
          All Rights Reserved 2025 | Easley-Dunn Games |{" "}
          <a
            href="http://easleydunnproductions.com/ContactUs.html"
            target="_blank"
            rel="noopener noreferrer"
            className="contact-link"
          >
            Contact Us
          </a>{" "}
          |{" "}
          <a
            href="https://easleydunnproductions.com/PrivacyPolicy.php"
            target="_blank"
            rel="noopener noreferrer"
            className="privacy-link"
          >
            Privacy Policy
          </a>{" "}
          | Website by Phred | Website Design by SpiderHandz
        </p>
        <p className="font-addendum">
          Dimbo font © 2009–2012 GrandChaos9000 (Jayvee D. Enaguas). Licensed under Creative Commons Attribution–ShareAlike 3.0 (CC BY-SA 3.0).
        </p>
      </footer>
    </div>
  );
};

export default Page4;